import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { generatePdfBlob } from "@/lib/pdfExport";

const SCALE_LABELS = ["Inexistente", "Informal", "Parcial", "Estruturado", "Avançado"];

const brl = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const AdminReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const autopdf = searchParams.get("autopdf") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pdfRootRef = useRef(null);
  const sentRef = useRef(false);

  useEffect(() => {
    api
      .get(`/diagnostics/${id}/full`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!autopdf || loading || !data || sentRef.current) return;
    sentRef.current = true;
    setTimeout(async () => {
      try {
        const blob = await generatePdfBlob(pdfRootRef.current, "relatorio.pdf");
        window.parent?.postMessage(
          { type: "pdf-ready", kind: "relatorio", blob },
          window.location.origin
        );
      } catch (err) {
        window.parent?.postMessage(
          { type: "pdf-error", kind: "relatorio", error: String(err?.message || err) },
          window.location.origin
        );
      }
    }, 700);
  }, [autopdf, loading, data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B8DB3]">
        <Loader2 className="animate-spin mr-2" /> Carregando relatório...
      </div>
    );
  }
  if (!data) return null;

  const { diagnostic: d, sections, questions } = data;
  const scoreMap = Object.fromEntries(
    d.answers
      .filter((a) => a.score !== null && a.score !== undefined)
      .map((a) => [a.question_id, a.score])
  );
  const sc = d.scoring;
  const cls = sc.classification || {};

  return (
    <div className={`min-h-screen ${embed ? "bg-white text-[#1C1F26]" : "bg-[#0e1015] text-white"}`}>
      {/* Toolbar (não vai pro PDF) */}
      {!embed && (
      <div className="print-hide sticky top-0 z-20 bg-[#050608]/95 border-b border-[#1C1F26] backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/admin/diagnosticos/${id}`)}
            className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26]"
          >
            <ArrowLeft size={16} className="mr-2" /> Voltar
          </Button>
          <Button
            onClick={() => window.print()}
            data-testid="export-pdf-btn"
            className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
          >
            <Printer size={16} className="mr-2" /> Exportar PDF
          </Button>
        </div>
      </div>
      )}

      {/* Conteúdo do PDF (light theme) */}
      <main
        ref={pdfRootRef}
        data-pdf-root="relatorio"
        className={`print-page ${embed ? "max-w-[210mm] mx-auto bg-white text-[#1C1F26]" : "max-w-[210mm] mx-auto bg-white text-[#1C1F26] my-6 shadow-2xl"}`}
      >
        <div className="p-10 print:p-0">
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-[#0A2342] pb-5 mb-6 print-divider">
            <div>
              <img src={LOGO_URL} alt="UniGR" style={{ height: 54 }} className="object-contain" />
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mt-2">
                Conhecimento que transforma
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#3A3F47]">
                Relatório de Diagnóstico
              </div>
              <div className="text-lg font-bold text-[#0A2342]">
                Maturidade em Gestão de Frota
              </div>
              <div className="text-xs text-[#4a5360] mt-1">
                Emitido em {new Date().toLocaleDateString("pt-BR")}
              </div>
            </div>
          </div>

          {/* Empresa */}
          <h1 className="text-3xl font-bold text-[#0A2342] mb-1">{d.company}</h1>
          <div className="text-sm text-[#4a5360] mb-6">
            CNPJ {d.cnpj || "—"} · Consultor(a) UniGR: {d.consultant || "—"} ·
            Auditoria: {d.audit_date || "—"} · {d.city_uf || "—"}
          </div>

          {/* Resumo executivo */}
          <div className="grid grid-cols-3 gap-3 mb-7">
            <ReportCard
              label="Score Ponderado"
              value={`${Math.round(sc.total)}%`}
              sub={sc.level}
              color={cls.color}
            />
            <ReportCard
              label="Dimensão mais crítica"
              value={sc.worst_section?.name || "—"}
              sub={
                sc.worst_section
                  ? `${Math.round(sc.worst_section.score)}% · ${sc.worst_section.level}`
                  : ""
              }
              color="#B9262B"
              small
            />
            <ReportCard
              label="Economia mensal estimada"
              value={`${brl(sc.economy?.min_value)} — ${brl(sc.economy?.max_value)}`}
              sub={`${sc.economy?.min_pct}% – ${sc.economy?.max_pct}% sobre controláveis`}
              color="#22B673"
              small
            />
          </div>

          {/* Score por sessão */}
          <SectionTitle>Score por Sessão</SectionTitle>
          <div className="space-y-3 mb-7">
            {sections.map((s) => {
              const si = sc.by_section?.[s.id];
              if (!si || si.total_questions === 0) return null;
              const sicls = si.classification || {};
              return (
                <div key={s.id} className="text-sm">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-[#0A2342] font-semibold">{s.name}</span>
                    <span className="font-mono text-xs text-[#4a5360]">
                      <span style={{ color: sicls.color }} className="font-bold">
                        {Math.round(si.score)}%
                      </span>
                      <span className="ml-2">· {sicls.level_short}</span>
                      <span className="ml-2">· peso {si.weight}%</span>
                    </span>
                  </div>
                  <div className="h-2 bg-[#e6e6e8] rounded-full overflow-hidden print-bar-bg">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${si.score}%`, background: sicls.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Indicadores Financeiros */}
          {sc.kpis && (
            <>
              <SectionTitle>Indicadores Financeiros Calculados</SectionTitle>
              <div className="grid grid-cols-3 gap-2 mb-7 text-xs">
                <KpiLine label="Custo total mensal" value={brl(sc.kpis.total_monthly_cost)} bold />
                <KpiLine label="Custos controláveis" value={brl(sc.kpis.controllable_costs)} bold />
                <KpiLine label="Receita mensal" value={sc.kpis.revenue != null ? brl(sc.kpis.revenue) : "—"} />
                <KpiLine label="Custo por veículo" value={brl(sc.kpis.cost_per_vehicle)} />
                <KpiLine label="Custo por km" value={brl(sc.kpis.cost_per_km)} />
                <KpiLine label="Combustível por km" value={brl(sc.kpis.fuel_cost_per_km)} />
                <KpiLine label="Manutenção por veículo" value={brl(sc.kpis.maintenance_per_vehicle)} />
                <KpiLine label="Pneus por veículo" value={brl(sc.kpis.tires_per_vehicle)} />
                <KpiLine label="Multas por veículo" value={brl(sc.kpis.fines_per_vehicle)} />
                <KpiLine label="Tecnologia por veículo" value={brl(sc.kpis.tech_per_vehicle)} />
                <KpiLine label="Acidentes (12m) / veículo" value={sc.kpis.accident_rate ?? "—"} />
                <KpiLine label="Roubos-furtos (12m) / veículo" value={sc.kpis.theft_rate ?? "—"} />
              </div>
            </>
          )}

          {/* Plano de Ação por sessão */}
          <SectionTitle>Plano de Ação Recomendado por Dimensão</SectionTitle>
          <div className="space-y-3 mb-7">
            {sections.map((s) => {
              const si = sc.by_section?.[s.id];
              if (!si || si.total_questions === 0 || !si.classification) return null;
              const c = si.classification;
              return (
                <div
                  key={s.id}
                  className="border border-[#cfd4dc] rounded p-3 text-xs print-card"
                  style={{ borderLeft: `3px solid ${c.color}` }}
                >
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="font-bold text-[#0A2342]">{s.name}</span>
                    <span className="text-[#4a5360]">
                      <span style={{ color: c.color }} className="font-bold">
                        {Math.round(si.score)}%
                      </span>{" "}
                      · {c.level} · Prioridade {c.priority} · {c.timeline}
                    </span>
                  </div>
                  <div className="text-[#1C1F26] leading-snug">
                    <span className="font-semibold">Recomendação:</span>{" "}
                    {c.product_recommendation}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detalhe das respostas */}
          <SectionTitle>Detalhe das Respostas</SectionTitle>
          <div className="space-y-4 text-xs">
            {sections.map((s) => {
              const sQs = questions.filter(
                (q) => q.section_id === s.id && (q.question_type || "scale") === "scale"
              );
              if (sQs.length === 0) return null;
              return (
                <div key={s.id} className="print-card">
                  <div className="text-[#0A2342] font-bold mb-1.5">{s.name}</div>
                  <table className="w-full">
                    <tbody>
                      {sQs.map((q) => {
                        const score = scoreMap[q.id];
                        return (
                          <tr key={q.id} className="border-b border-[#e6e6e8] last:border-0">
                            <td className="py-1 pr-2">{q.text}</td>
                            <td className="py-1 text-right font-mono whitespace-nowrap">
                              {score !== undefined
                                ? `${score}/4 · ${SCALE_LABELS[score]}`
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          <div className="mt-8 pt-4 border-t border-[#cfd4dc] text-[10px] text-[#4a5360] flex justify-between">
            <span>UniGR — Conhecimento que transforma</span>
            <span>Protocolo: {d.id}</span>
          </div>
        </div>
      </main>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-[#0A2342] border-b border-[#cfd4dc] pb-1 mb-3 mt-2">
    {children}
  </h2>
);

const ReportCard = ({ label, value, sub, color, small }) => (
  <div
    className="border border-[#cfd4dc] rounded p-3 print-card"
    style={{ borderTop: `3px solid ${color}` }}
  >
    <div className="text-[9px] uppercase tracking-wider text-[#4a5360] mb-1">
      {label}
    </div>
    <div
      className={small ? "text-sm font-semibold leading-tight" : "text-2xl font-bold leading-tight"}
      style={{ color }}
    >
      {value}
    </div>
    {sub && <div className="text-[10px] text-[#4a5360] mt-1">{sub}</div>}
  </div>
);

const KpiLine = ({ label, value, bold }) => (
  <div className="border border-[#cfd4dc] rounded p-2 print-card flex justify-between gap-2">
    <span className="text-[#4a5360]">{label}</span>
    <span className={`font-mono ${bold ? "font-bold text-[#22B673]" : "text-[#1C1F26]"}`}>
      {value}
    </span>
  </div>
);

export default AdminReport;
